"use client";

import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import { savedItemsApi } from "@/lib/api/services/saved-items.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Bookmark, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Pagination } from "@/components/shared/pagination";
import { toast } from "@/components/ui/toast";

interface SavedItem {
  id: string;
  title: string;
  type: string;
  itemType?: string;
  itemId?: string;
  url: string;
  savedAt: string;
}

export default function SavedPage() {
  const [page, setPage] = useState(1);
  const perPage = 20;
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["saved-items", page],
    queryFn: async () => {
      const res = await savedItemsApi.list({ page, perPage });
      const payload = res as unknown as { data?: SavedItem[]; meta?: { total: number; totalPages: number } };
      return {
        items: payload.data ?? [],
        total: payload.meta?.total ?? 0,
        totalPages: payload.meta?.totalPages ?? 1,
      };
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const unsaveMutation = useMutation({
    mutationFn: (item: SavedItem) => savedItemsApi.unsave(item.itemType ?? item.type, item.itemId ?? item.id),
    onMutate: async (item) => {
      await queryClient.cancelQueries({ queryKey: ["saved-items", page] });
      const previous = queryClient.getQueryData(["saved-items", page]);
      queryClient.setQueryData(["saved-items", page], (old: typeof data | undefined) => {
        if (!old) return old;
        return { ...old, items: old.items.filter((i) => i.id !== item.id) };
      });
      return { previous };
    },
    onError: (_err, _item, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["saved-items", page], context.previous);
      }
      toast({ type: "error", title: "Error", message: "Could not remove saved item." });
    },
    onSuccess: () => {
      toast({ type: "success", title: "Removed", message: "Item removed from saved." });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-items"] });
    },
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="space-y-1 mb-6">
        <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground">Saved Items</h1>
        <p className="text-sm text-muted-foreground leading-relaxed">Content you have bookmarked for later.</p>
      </div>

      {isLoading ? (
        <div className="space-y-3" aria-busy="true">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="h-4 w-2/3 animate-pulse rounded bg-muted/10" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Bookmark className="mx-auto h-10 w-10 text-muted-foreground/30" />
            <h3 className="mt-4 font-heading text-base font-semibold text-foreground">No saved items</h3>
            <p className="mt-1 text-sm text-muted-foreground">Bookmark content to save it here.</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link href="/blogs">Browse blogs</Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link href="/discussions">Discussions</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {items.map((item) => (
              <Card key={item.id}>
                <CardContent className="flex items-center justify-between gap-3 p-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <Bookmark className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <Link href={item.url || "#"} className="text-sm font-medium text-foreground hover:text-primary transition-colors truncate block">{item.title}</Link>
                      <p className="text-xs text-muted-foreground">
                        {item.type}
                        {item.savedAt ? ` — ${new Date(item.savedAt).toLocaleDateString()}` : ""}
                      </p>
                    </div>
                  </div>
                  <ConfirmDialog
                    title="Remove saved item?"
                    description={`"${item.title}" will be removed from your saved list.`}
                    confirmLabel="Remove"
                    variant="destructive"
                    onConfirm={() => unsaveMutation.mutate(item)}
                  >
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label={`Remove ${item.title}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </ConfirmDialog>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-6">
            <Pagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} perPage={perPage} />
          </div>
        </>
      )}
    </div>
  );
}
